/**
 * Session — the reconciler. One pipeline: Firebase Auth identity resolves
 * the caller's grants and role into an actor projection; the sign-in
 * wizard's signAction$ channel (sign.state.ts, @momo/views) drives the
 * matching Firebase Auth + Firestore calls session.api.ts wraps. A host
 * mounts it once at boot with its own Firebase instances — mountSession —
 * and it runs for the document's whole lifetime; `.view`/`.state` stay
 * I/O-free, so `<sign-in>` answers in any host that mounted it.
 */
import { clearStates } from "@mhmo91/schmancy/state";
import {
	passkeyEnrolled,
	passkeyPhase,
	type SessionState,
	type SignAction,
	session,
	sessionReady,
	sessionRetry$,
	signAction$,
	signResult$,
} from "@momo/views/auth";
import { consoleEventId } from "@momo/views/console";
import { setLocale } from "@momo/views/localize";
import { organizationId } from "@momo/views/organization";
import type { User as FirebaseUser } from "firebase/auth";
import {
	catchError,
	combineLatest,
	defer,
	distinctUntilChanged,
	EMPTY,
	filter,
	from,
	map,
	merge,
	mergeMap,
	type Observable,
	of,
	retry,
	type Subscription,
	scan,
	startWith,
	switchMap,
	tap,
	timer,
} from "rxjs";
import { type SessionIdentity, sessionIdentity } from "./identity.api.ts";
import { legalReads } from "./legal.api.ts";
import { mountLegal } from "./legal.sync.ts";
import {
	InviteExpiredError,
	RateLimitedError,
	type SessionFirebase,
	sessionApi,
} from "./session.api.ts";
import { workspaceCard$ } from "./workspace.api.ts";
import { mountWorkspaceCard } from "./workspace.sync.ts";

/** What a host hands mountSession: its one Firebase app's instances, and the
 *  hooks only some hosts want. */
export type SessionHost = SessionFirebase & {
	/** The identity streams, when the host already reads them elsewhere;
	 *  built from `auth`/`db` otherwise. */
	readonly identity?: Pick<
		SessionIdentity,
		| "authUser$"
		| "currentUserDoc$"
		| "currentMemberships$"
		| "currentEventGrants$"
	>;
	/** A sign-in is announced and about to land (Revent web's Kevin boot):
	 *  the cold boot waits for it instead of bootstrapping anonymously. */
	readonly awaitAnnouncedSignIn?: boolean;
	/** Open the organization a sign-in's verify named when the document has
	 *  none open — for a host with no organization routing of its own. Revent
	 *  web leaves it off: its organization reconciler follows that answer. */
	readonly opensNamedOrganization?: boolean;
	/** Mint an anonymous account for a visitor once an organization is open —
	 *  a storefront host's buyer session (Revent web). Any other host never
	 *  creates one: its visitor stays signed out until they sign in. */
	readonly anonymousVisitors?: boolean;
	/** The host reads the open organization's card and legal texts itself
	 *  (Revent web, through @momo/session/legal). Any other host has the
	 *  reconciler read them, so the consent step can offer the terms. */
	readonly readsOrganization?: boolean;
};

// Live route-guard signal for the admin route (app-index.view.ts). Continuous,
// not take(1): schmancy re-evaluates a `.guard=` on every emission, so a
// session-ending flip (sign-out, an mfa-claim downgrade) unmounts admin the
// instant sessionReady goes false — before any child view's own data read can
// render against the lost session.
// "binding" is pending too: the organization changed and its role has not
// answered, so the guard neither unmounts nor redirects on it.
export const signedInGate$ = session.$.pipe(
	filter((s) => s.status !== "loading" && s.status !== "binding"),
	map(() => sessionReady.get()),
	// The router re-delivers a guarded route's last navigation on every verdict,
	// and the session re-answers on any change (an opened event among them), so
	// only a changed verdict is stated.
	distinctUntilChanged(),
);

// Anonymous-bootstrap retry budget. Exponential backoff capped at RETRY_CAP_MS
// so a persistent auth-endpoint failure keeps retrying without hammering (the
// self-DoS shape), plus equal jitter so a fleet recovering together doesn't
// retry in lockstep. After RETRY_MAX_ATTEMPTS the reconciler gives up and lands
// session.status on "error": a transient blip (offline, emulator still booting)
// self-recovers inside the ~60s budget, and only a persistent failure reaches
// the visible curtain, which offers a manual retry via sessionRetry$.
/** The scope a code was sent under: the address and the organization it was
 *  sent for, null before any send in this document. */
type SentUnder = {
	readonly email: string;
	readonly orgId: string | null;
} | null;

const RETRY_BASE_MS = 1_000;
const RETRY_CAP_MS = 30_000;
const RETRY_MAX_ATTEMPTS = 6;

// The two non-resolved session shapes the reconciler emits directly (as opposed
// to the anonymous/member shapes it computes) — differing only in `status`.
// Typed against SessionState so a shape change fails the build here.
const LOADING_IDENTITY: SessionState = {
	status: "loading",
	uid: null,
	email: null,
	needsConsent: false,
	actor: null,
};
const SIGNED_OUT: SessionState = {
	status: "anonymous",
	uid: null,
	email: null,
	needsConsent: false,
	actor: null,
};
const ERROR_IDENTITY: SessionState = {
	status: "error",
	uid: null,
	email: null,
	needsConsent: false,
	actor: null,
};

/** Mounts the reconciler; call once at boot. The returned subscription ends
 *  it (a test's teardown; a host never needs to). */
export function mountSession(host: SessionHost): Subscription {
	const { auth } = host;
	const {
		adoptOrganizationIfUnset,
		enrollPasskey,
		signInWithPasskey,
		fetchMfaClaim,
		fetchRole,
		resolveEmail,
		sendOtp,
		signInAnonymousSession,
		signOutSession,
		verifyOtp,
		writeUserConsent,
	} = sessionApi(host);
	const {
		authUser$,
		currentEventGrants$,
		currentMemberships$,
		currentUserDoc$,
	} = host.identity ?? sessionIdentity(host);
	const kevinSignInAnnounced = host.awaitAnnouncedSignIn === true;

	// defer, not from — from(promise) would cache the one rejected
	// promise and re-fail every retry; defer re-invokes the sign-in per
	// attempt. startWith re-asserts "loading" on every (re)entry — first
	// boot and a manual retry alike — clearing a prior "error" through
	// the one write path below. The retry budget is spent silently while
	// "loading" (the storefront is browsable throughout); only its final
	// rejection falls to catchError and lands the visible error state.
	// A signed-in Kevin boot names its account before the app's first script and
	// signs in with a custom token moments later. Bootstrapping anonymously in
	// that gap settles the session as signed out, and the admin guard sends the
	// boot to sign-in before its user lands (host.awaitAnnouncedSignIn).
	// A verified session is never replaced: Firebase updates currentUser
	// before its listeners hear of it, so an organization opened by the verify
	// can reach the signed-out branch first. That user arrives through
	// authUser$ a beat later; the bootstrap stands down for it.
	const anonymousBootstrap$ = defer(() =>
		auth.currentUser !== null && !auth.currentUser.isAnonymous
			? EMPTY
			: signInAnonymousSession(),
	).pipe(
		map(() => null),
		retry({
			count: RETRY_MAX_ATTEMPTS,
			delay: (err: unknown, attempt: number) => {
				const ceiling = Math.min(
					RETRY_BASE_MS * 2 ** (attempt - 1),
					RETRY_CAP_MS,
				);
				const backoff = ceiling / 2 + Math.random() * (ceiling / 2);
				console.warn(
					`[session] anonymous sign-in failed (attempt ${attempt}/${RETRY_MAX_ATTEMPTS}) — retrying in ${Math.round(backoff)}ms`,
					err,
				);
				return timer(backoff);
			},
		}),
		catchError((err: unknown) => {
			console.error(
				"[session] anonymous sign-in gave up after the retry budget — surfacing the connection error",
				err,
			);
			return of(ERROR_IDENTITY);
		}),
		startWith(LOADING_IDENTITY),
	);

	const mounted = merge(
		// idTokenUser$ drives on a real Firebase Auth change; sessionRetry$ re-drives
		// the same chain on a manual retry from the error curtain, re-injecting the
		// current auth user (null once a failed anonymous sign-in left none) so the
		// null branch below re-attempts the bootstrap with a fresh backoff budget.
		merge(authUser$, sessionRetry$.pipe(map(() => auth.currentUser))).pipe(
			// A different identity taking over this device (a member signing out to
			// a buyer session, or a second member signing in) must not inherit the
			// prior identity's state; a token refresh of the same uid clears nothing.
			// Signing in from no identity takes over nothing: the sign-out that led
			// there already cleared, and what the verify has just written for this
			// person (the organization it named) must survive their user landing.
			scan(
				(previous, user) => ({
					user,
					uid: user?.uid ?? null,
					cold: previous.uid === undefined,
					changed:
						previous.uid !== undefined &&
						previous.uid !== null &&
						previous.uid !== (user?.uid ?? null),
				}),
				// uid undefined: no identity has been established on this device yet.
				{ user: null, uid: undefined, cold: true, changed: false } as {
					user: FirebaseUser | null;
					uid: string | null | undefined;
					cold: boolean;
					changed: boolean;
				},
			),
			switchMap(({ user, cold, changed }) =>
				(changed ? clearStates().pipe(map(() => user)) : of(user)).pipe(
					map((resolved) => ({ user: resolved, cold })),
				),
			),
			switchMap(({ user, cold }) => {
				if (user === null) {
					// No organization, no anonymous account: the bare host serves no
					// storefront, so a visitor there (a crawler included) is never
					// minted one. The email sign-in needs none either. An organization
					// chosen later in this document starts the bootstrap then.
					return organizationId.$.pipe(
						map((orgId) => orgId !== null),
						distinctUntilChanged(),
						// Only the cold boot waits: Kevin's user arrives through
						// authUser$ and the outer switchMap drops the wait; a later
						// sign-out in the same document is bootstrapped at once. The
						// timer bounds a Kevin sign-in that never lands.
						switchMap((hasOrganization) =>
							!hasOrganization || host.anonymousVisitors !== true
								? of(SIGNED_OUT)
								: cold && kevinSignInAnnounced
									? timer(60_000).pipe(
											switchMap(() => anonymousBootstrap$),
											startWith(LOADING_IDENTITY),
										)
									: anonymousBootstrap$,
						),
					);
				}
				if (user.isAnonymous) {
					return of({
						status: "anonymous" as const,
						uid: user.uid,
						email: null,
						needsConsent: false,
						actor: null,
					});
				}
				// Keyed on the organization: each one starts with its actor unanswered
				// ("binding"), so the previous organization's actor never stands beside
				// this one while its record and role are read.
				return organizationId.$.pipe(
					distinctUntilChanged(),
					switchMap((orgId) =>
						combineLatest([
							// A transient read failure must stay distinct from a confirmed
							// missing doc: folding both to null reads as needsConsent, the
							// consent screen shows for a returning member, and accepting it
							// setDoc-overwrites their original user doc (fresh createdAt,
							// re-recorded consents). Live listener, not a one-shot getDoc: a
							// plain Firestore write triggers no idTokenUser$ re-emission, so a
							// one-shot read would never observe createAccount's setDoc and
							// needsConsent would stay stuck true until a reload.
							currentUserDoc$.pipe(
								filter((answer) => answer.uid === user.uid),
								map((answer) => answer.user),
								// The record's own content, not its sync metadata, is what
								// the session answers to.
								distinctUntilChanged(
									(a, b) => JSON.stringify(a) === JSON.stringify(b),
								),
								catchError(() => of("unavailable" as const)),
							),
							currentMemberships$.pipe(
								filter((answer) => answer.uid === user.uid),
								map((answer) => answer.grants),
								catchError(() => of([])),
							),
							currentEventGrants$.pipe(
								filter((answer) => answer.uid === user.uid),
								catchError(() => of(null)),
							),
							consoleEventId.$,
							from(fetchMfaClaim(user)).pipe(catchError(() => of(false))),
						]).pipe(
							map(([userDoc, grants, eventGrants, eventId, mfa]) => ({
								// Undefined while the event grants answered for another
								// organization than this one; a failed read grants nothing.
								eventGrants:
									eventGrants === null
										? []
										: eventGrants.orgId === orgId
											? eventGrants.grants
											: undefined,
								eventId,
								orgId,
								memberships: grants.map((grant) => grant.id),
								userDoc,
								mfa,
								workspaces: Object.fromEntries(
									grants
										.filter(
											(g): g is typeof g & { roleId: string } =>
												g.roleId !== null,
										)
										.map((g) => [g.id, g.roleId ?? null] as const),
								),
							})),
							// The account-level language reconciles into the live locale here,
							// while userDoc is still the full parsed doc — the projection below
							// drops it down to `identity`, which carries no language field.
							// Re-fires on every users/{uid} write the live listener re-emits, not
							// only this one; setLocale with an unchanged value is idempotent, so
							// that's left as-is. Bare setLocale, not setUiLanguage — applying the
							// account's own stored value is not a fresh user choice, so it must
							// not re-persist to the device-tier localStorage key.
							tap((carry) => {
								if (
									carry.userDoc !== "unavailable" &&
									carry.userDoc?.language
								) {
									setLocale(carry.userDoc.language);
								}
							}),
							switchMap((carry) =>
								defer((): Observable<SessionState | null> => {
									if (carry.userDoc === "unavailable") {
										// The record cannot be read, so neither consent nor an
										// actor can be stated: the connection curtain says so,
										// and its retry re-drives this chain with a fresh read.
										console.warn(
											"[session] users/{uid} read failed — surfacing the connection error",
										);
										return of(ERROR_IDENTITY);
									}
									const needsConsent = carry.userDoc === null;
									// No organization binds no actor: there is nothing to act in.
									if (carry.orgId === null)
										return of({
											status: "member" as const,
											uid: user.uid,
											email: user.email,
											needsConsent,
											actor: null,
										});
									// This organization's event grants have not answered yet.
									if (carry.eventGrants === undefined) return of(null);
									const orgId = carry.orgId;
									const roleId = carry.workspaces[orgId] ?? null;
									const eventRoleId =
										carry.eventGrants.find(
											(grant) => grant.eventId === carry.eventId,
										)?.roleId ?? null;
									return combineLatest([
										roleId ? from(fetchRole(orgId, roleId)) : of(null),
										carry.eventId && eventRoleId
											? from(fetchRole(orgId, eventRoleId))
											: of(null),
									]).pipe(
										catchError(() => of([null, null] as const)),
										map(([role, eventRole]) => ({
											status: "member" as const,
											uid: user.uid,
											email: user.email,
											needsConsent,
											actor: {
												uid: user.uid,
												orgId,
												eventId: carry.eventId,
												eventPermissions: eventRole?.permissions ?? [],
												memberships: carry.memberships,
												mfa: carry.mfa,
												permissions: role?.permissions ?? [],
												workspaces: carry.workspaces,
											},
										})),
									);
								}),
							),
							// Unanswered the moment the organization changes, before
							// any read of the new one has answered: the previous
							// organization's actor never stands while these load.
							// Consent is not known yet either; every reader treats
							// "binding" as pending, so its needsConsent states nothing.
							// An ID-token refresh of the identity already bound to this
							// organization re-reads everything (an mfa claim can drop)
							// without unbinding it: nothing changed hands.
							(reads$: Observable<SessionState | null>) =>
								defer(() =>
									session.value.status === "member" &&
									session.value.uid === user.uid &&
									(session.value.actor?.orgId ?? null) === orgId
										? reads$
										: reads$.pipe(
												startWith<SessionState | null>({
													status: "binding",
													uid: user.uid,
													email: user.email,
													needsConsent: false,
													actor: null,
												}),
											),
								),
						),
					),
				);
			}),
			filter((identity) => identity !== null),
			tap((identity) => session.set(identity)),
		),

		// mergeMap: dispatches are keyed by requestId, so one in-flight action
		// must not drop an independent action for a different request. The scope a
		// code was sent under rides the stream to its verify, which must ask under
		// the same scope even when the organization cell has moved since.
		signAction$.pipe(
			scan<
				SignAction,
				{ readonly action: SignAction; readonly sentUnder: SentUnder },
				{ readonly sentUnder: SentUnder }
			>(
				(carry, action) => ({
					action,
					sentUnder:
						action.kind === "sendOtp"
							? { email: action.email, orgId: organizationId.value }
							: carry.sentUnder,
				}),
				{ sentUnder: null },
			),
			mergeMap(({ action, sentUnder }) => {
				switch (action.kind) {
					// Asked under the organization open now — the scope the send that
					// follows it is recorded under.
					case "resolveEmail":
						return from(resolveEmail(organizationId.value, action.email)).pipe(
							tap((result) =>
								signResult$.next({
									requestId: action.requestId,
									kind: action.kind,
									ok: true,
									known: result.known,
									invite: result.invite ?? undefined,
								}),
							),
							catchError((err: unknown) => {
								console.error(
									"[session] resolveEmail failed",
									action.requestId,
									err,
								);
								signResult$.next({
									requestId: action.requestId,
									kind: action.kind,
									ok: false,
									reason:
										err instanceof RateLimitedError
											? "rate-limited"
											: undefined,
								});
								return of(null);
							}),
						);
					case "sendOtp":
						return from(sendOtp(sentUnder?.orgId ?? null, action.email)).pipe(
							tap((result) =>
								signResult$.next({
									requestId: action.requestId,
									kind: action.kind,
									ok: true,
									invite: result.invite ?? undefined,
								}),
							),
							catchError((err: unknown) => {
								console.error(
									"[session] sendOtp failed",
									action.requestId,
									err,
								);
								signResult$.next({
									requestId: action.requestId,
									kind: action.kind,
									ok: false,
									reason:
										err instanceof RateLimitedError
											? "rate-limited"
											: undefined,
								});
								return of(null);
							}),
						);
					case "verifyOtp":
						return from(
							verifyOtp(
								sentUnder?.email === action.email
									? sentUnder.orgId
									: organizationId.value,
								action.email,
								action.code,
							),
						).pipe(
							// A returning person whose record names no organization has the
							// one this sign-in opened saved onto it; a new person's record
							// is born with it at consent. A founder on a listed domain is
							// signed in with no organization yet: nothing to adopt.
							switchMap((result) =>
								result.isNewUser ||
								auth.currentUser === null ||
								result.workspaceId === undefined
									? of(result)
									: from(
											adoptOrganizationIfUnset(
												auth.currentUser.uid,
												result.workspaceId,
											),
										).pipe(
											catchError((err: unknown) => {
												console.warn(
													"[session] saving the opened organization failed",
													err,
												);
												return of(undefined);
											}),
											map(() => result),
										),
							),
							tap((result) => {
								signResult$.next({
									requestId: action.requestId,
									kind: action.kind,
									ok: true,
									isNewUser: result.isNewUser,
									uid: auth.currentUser?.uid,
									displayName: result.displayName ?? undefined,
									workspaceId: result.workspaceId,
								});
							}),
							catchError((err: unknown) => {
								console.error(
									"[session] verifyOtp failed",
									action.requestId,
									err,
								);
								// verifyOtp() (session.api.ts) rethrows verifyOTP.api.ts's
								// `failed-precondition/invite-expired` as this typed error —
								// thrown only AFTER the code's own HMAC already matched, so
								// naming the real cause here widens no enumeration signal;
								// every other failure (wrong/expired code) stays generic below.
								const reason =
									err instanceof InviteExpiredError
										? ("invite-expired" as const)
										: undefined;
								signResult$.next({
									requestId: action.requestId,
									kind: action.kind,
									ok: false,
									reason,
								});
								return of(null);
							}),
						);
					case "passkeySignIn":
						return from(
							signInWithPasskey({
								...(action.mediation ? { mediation: action.mediation } : {}),
								...(action.signal ? { signal: action.signal } : {}),
							}),
						).pipe(
							tap((result) =>
								signResult$.next({
									requestId: action.requestId,
									kind: action.kind,
									ok: true,
									uid: auth.currentUser?.uid,
									workspaceId:
										typeof result.workspaceId === "string"
											? result.workspaceId
											: undefined,
								}),
							),
							catchError((err: unknown) => {
								signResult$.next({
									requestId: action.requestId,
									kind: action.kind,
									ok: false,
									reason:
										err instanceof DOMException &&
										(err.name === "NotAllowedError" ||
											err.name === "AbortError")
											? "cancelled"
											: undefined,
								});
								return of(null);
							}),
						);
					case "enrollPasskey": {
						const uid = auth.currentUser?.uid;
						passkeyPhase.set("working");
						return from(enrollPasskey()).pipe(
							tap(() => {
								if (uid && auth.currentUser?.uid === uid)
									passkeyEnrolled.set(uid);
								passkeyPhase.set("done");
								signResult$.next({
									requestId: action.requestId,
									kind: action.kind,
									ok: true,
								});
							}),
							catchError(() => {
								passkeyPhase.set("error");
								signResult$.next({
									requestId: action.requestId,
									kind: action.kind,
									ok: false,
								});
								return of(null);
							}),
						);
					}
					case "createAccount":
						return from(
							writeUserConsent({
								uid: action.uid,
								email: action.email,
								displayName: action.displayName,
								workspaceId: action.workspaceId,
								tosVersion: action.tosVersion,
								privacyVersion: action.privacyVersion,
								platform: action.platform,
							}),
						).pipe(
							tap(() =>
								signResult$.next({
									requestId: action.requestId,
									kind: action.kind,
									ok: true,
								}),
							),
							catchError((err: unknown) => {
								console.error(
									"[session] createAccount failed",
									action.requestId,
									err,
								);
								signResult$.next({
									requestId: action.requestId,
									kind: action.kind,
									ok: false,
									error:
										err instanceof Error
											? err.message
											: "Account creation failed. Try again.",
								});
								return of(null);
							}),
						);
					case "signOut":
						return from(signOutSession()).pipe(
							tap(() =>
								signResult$.next({
									requestId: action.requestId,
									kind: action.kind,
									ok: true,
								}),
							),
							catchError((err: unknown) => {
								console.error(
									"[session] signOut failed",
									action.requestId,
									err,
								);
								signResult$.next({
									requestId: action.requestId,
									kind: action.kind,
									ok: false,
								});
								return of(null);
							}),
						);
				}
			}),
		),

		// A host with no organization routing of its own opens the organization
		// the server named, so the actor binds there and the sign-in completes.
		host.opensNamedOrganization === true
			? signResult$.pipe(
					filter(
						(result) =>
							(result.kind === "verifyOtp" ||
								result.kind === "passkeySignIn") &&
							result.ok &&
							result.workspaceId !== undefined &&
							organizationId.value === null,
					),
					tap((result) => organizationId.set(result.workspaceId ?? null)),
				)
			: EMPTY,
	).subscribe();
	if (host.readsOrganization !== true) {
		mounted.add(mountWorkspaceCard((orgId) => workspaceCard$(host.db, orgId)));
		mounted.add(
			mountLegal(
				legalReads({
					db: host.db,
					storageBucket: host.db.app.options.storageBucket,
				}),
			),
		);
	}
	return mounted;
}
